import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate, Link } from 'react-router-dom';
import { LayoutDashboard, Truck, Mail, Settings, LogOut, Menu, ExternalLink, FileText, ShieldCheck } from 'lucide-react';
import { getUser, api } from '../lib/api';
import { Logo } from '../components/Logo';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';

const NAV = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true, testid: 'admin-nav-dashboard' },
  { to: '/admin/vehicles', label: 'Vehicles', icon: Truck, testid: 'admin-nav-vehicles' },
  { to: '/admin/enquiries', label: 'Enquiries', icon: Mail, testid: 'admin-nav-enquiries' },
  { to: '/admin/content', label: 'Site Content', icon: FileText, testid: 'admin-nav-content' },
  { to: '/admin/audit', label: 'Audit Log', icon: ShieldCheck, testid: 'admin-nav-audit' },
  { to: '/admin/settings', label: 'Settings', icon: Settings, testid: 'admin-nav-settings' },
];

const SidebarNav = ({ onNavigate, onLogout }) => (
  <div className="flex flex-col h-full">
    <div className="px-5 py-6">
      <Logo to="/admin" />
    </div>
    <nav className="flex-1 px-3 space-y-1">
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          data-testid={item.testid}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
              isActive ? 'bg-[#E4002B] text-white' : 'text-white/70 hover:text-white hover:bg-white/10'
            }`
          }
        >
          <item.icon className="h-4 w-4" />
          {item.label}
        </NavLink>
      ))}
    </nav>
    <div className="px-3 pb-6 space-y-1">
      <Link
        to="/"
        className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-white/50 hover:text-white hover:bg-white/10 transition-colors duration-150"
      >
        <ExternalLink className="h-4 w-4" />
        View Website
      </Link>
      <button
        data-testid="admin-logout-button"
        onClick={onLogout}
        className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors duration-150"
      >
        <LogOut className="h-4 w-4" />
        Logout
      </button>
    </div>
  </div>
);

export const AdminLayout = () => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const user = getUser();
  const token = localStorage.getItem('hts_token');

  useEffect(() => {
    if (!token) navigate('/admin/login', { replace: true });
  }, [token, navigate]);

  if (!token) return null;

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout'); // revoke token server-side
    } catch {
      // token may already be expired - proceed with local cleanup
    }
    localStorage.removeItem('hts_token');
    localStorage.removeItem('hts_user');
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-[#D9D9D6]/25 flex">
      <aside className="hidden lg:flex w-60 bg-[#24135F] flex-col fixed inset-y-0 left-0 z-40">
        <SidebarNav onLogout={handleLogout} />
      </aside>
      <div className="flex-1 lg:ml-60 flex flex-col min-h-screen">
        <header className="bg-white border-b border-[#D9D9D6] h-14 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden p-2 rounded-lg hover:bg-[#D9D9D6]/30" aria-label="Open admin menu" data-testid="admin-mobile-menu-button">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-[#24135F] border-white/10 w-64 p-0">
                <SidebarNav onNavigate={() => setMobileOpen(false)} onLogout={handleLogout} />
              </SheetContent>
            </Sheet>
            <span className="text-sm font-semibold text-[#53565A]">Admin Portal</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-[#E4002B] text-white flex items-center justify-center text-xs font-bold">
              {(user?.name || 'A').charAt(0).toUpperCase()}
            </div>
            <div className="hidden sm:block">
              <p className="text-xs font-semibold text-[#24135F] leading-tight">{user?.name || 'Admin'}</p>
              <p className="text-[10px] text-[#53565A]/70">Admin</p>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
